import type { ReactNode } from 'react'
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { motion, useAnimationFrame, useReducedMotion } from 'motion/react'

import type { Icon } from '@slite/icons'
import {
  ChatGPTIcon,
  ClaudeIcon,
  CursorIcon,
  GeminiIcon,
  LovableIcon,
} from '@slite/icons'

type Orientation = 'horizontal' | 'vertical'

type Point = { x: number; y: number }

type Size = { w: number; h: number }

export type ConnectorLogo = {
  Icon?: Icon
  /** Custom element rendered instead of Icon (e.g. an avatar cluster) */
  node?: ReactNode
  u: number
  c: number
}

type CircuitBoardConnectorProps = {
  orientation: Orientation
  /**
   * Cross-axis trace endpoints along the shared server-card edge — px, or
   * fractions of the edge with `relativeCross`.
   */
  targets: number[]
  /** Logos to fan out from the cluster; defaults to the AI-client set */
  logos?: ConnectorLogo[]
  /**
   * Flip the geometry: logos sit on the far side of the flow axis. Also flips
   * the default signal direction to 'out' (override with `flow`).
   */
  mirror?: boolean
  /**
   * Signal direction along the traces: 'in' runs logos → server edge, 'out'
   * runs server edge → logos. Defaults follow the geometry ('out' when
   * mirrored), so it only needs setting when the two must differ — e.g. a
   * vertical stack where every signal travels downward.
   */
  flow?: 'in' | 'out'
  /**
   * Conducted mode: stop self-spawning; spawn `count` signals per
   * emit(count) on the relay instead. Lets a parent run one master clock
   * across several connectors.
   */
  spawnSource?: SignalRelay
  /** Fixed travel time for conducted signals (default: random). */
  signalDurationMs?: number
  /**
   * Interpret logo `c` and `targets` as fractions (0–1) of the cross-axis
   * extent instead of px — for fluid-width fans that track a resizing edge.
   */
  relativeCross?: boolean
  /** Side of the square vias marking both trace ends */
  viaSize?: number
  /** How far trace ends stop short of the server-card edge */
  endInset?: number
}

export type SignalRelay = {
  emit: (count?: number) => void
  subscribe: (listener: (count: number) => void) => () => void
}

export function createSignalRelay(): SignalRelay {
  const listeners = new Set<(count: number) => void>()
  return {
    emit: (count = 1) => {
      for (const listener of listeners) listener(count)
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

const TRACE_COLOR = '#F0E4D6'
const TRACE_WIDTH = 2
const LOGO_SIZE = 30
const CLUSTER_DEPTH = 170
const END_INSET = 4
const START_VIA_GAP = 12
const VIA_SIZE = 5
const ELBOW_RADIUS = 10
const CHANNEL_FRACTIONS = [0.34, 0.82, 0.18, 0.66, 0.5]

const MAX_DOTS = 10
const DOT_RADIUS = 2
const DOT_COLOR = 'var(--saturn-orange)'
const DOT_OPACITY = 0.95
const FADE_FRACTION = 0.12
const TRAIL_LENGTH = 80
const TRAIL_MAX_FRACTION = 0.35
const TRAIL_WIDTH = 2
const TRAIL_OPACITY = 0.5
const RUN_DURATION_MS: [number, number] = [2500, 5000]
const SPAWN_GAP_MS: [number, number] = [1500, 4500]
const FIRST_SPAWN_MS: [number, number] = [400, 2000]

// The AI clients that read verified context out of Slite — the default set.
const AI_CLIENT_LOGOS: ConnectorLogo[] = [
  { Icon: ClaudeIcon, u: 32, c: 44 },
  { Icon: LovableIcon, u: 105, c: 88 },
  { Icon: GeminiIcon, u: 56, c: 148 },
  { Icon: ChatGPTIcon, u: 118, c: 204 },
  { Icon: CursorIcon, u: 36, c: 256 },
]

// Geometry is authored in (u, c) space: u runs along the signal-flow axis
// from the logo edge (0) to the server edge, c is the cross-axis coordinate
// shared with the server card edge. Logos sit at fixed u so the cluster
// keeps its size — only the trace zone absorbs container resizes. `mirror`
// reflects u so the same authoring drives a right-hand (server → logos) fan.
function toPixel(
  u: number,
  c: number,
  orientation: Orientation,
  size: Size,
  mirror: boolean
): Point {
  if (orientation === 'horizontal') {
    return { x: mirror ? size.w - u : u, y: c }
  }
  return { x: c, y: mirror ? u : size.h - u }
}

function buildRoundedPath(points: Point[], radius: number): string {
  const round = (value: number) => Math.round(value * 100) / 100
  let d = `M ${round(points[0].x)} ${round(points[0].y)}`
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]
    const corner = points[i]
    const next = points[i + 1]
    const inLength = Math.hypot(corner.x - prev.x, corner.y - prev.y)
    const outLength = Math.hypot(next.x - corner.x, next.y - corner.y)
    if (inLength === 0 || outLength === 0) continue
    const r = Math.min(radius, inLength / 2, outLength / 2)
    const entryX = corner.x - ((corner.x - prev.x) / inLength) * r
    const entryY = corner.y - ((corner.y - prev.y) / inLength) * r
    const exitX = corner.x + ((next.x - corner.x) / outLength) * r
    const exitY = corner.y + ((next.y - corner.y) / outLength) * r
    d += ` L ${round(entryX)} ${round(entryY)} Q ${round(corner.x)} ${round(corner.y)} ${round(exitX)} ${round(exitY)}`
  }
  const last = points[points.length - 1]
  d += ` L ${round(last.x)} ${round(last.y)}`
  return d
}

type TraceGeometry = { d: string; startVia: Point; endVia: Point }

const crossExtent = (size: Size, orientation: Orientation) =>
  orientation === 'horizontal' ? size.h : size.w

function computeTraces(
  size: Size,
  orientation: Orientation,
  targets: number[],
  logos: ConnectorLogo[],
  mirror: boolean,
  relativeCross: boolean,
  endInset: number
): TraceGeometry[] {
  const flowLength = orientation === 'horizontal' ? size.w : size.h
  const zoneEnd = flowLength - endInset
  const zone = Math.max(zoneEnd - CLUSTER_DEPTH, 40)
  const resolveC = (value: number) =>
    relativeCross ? value * crossExtent(size, orientation) : value

  return logos.map((logo, index) => {
    const target = resolveC(targets[index % targets.length])
    const logoC = resolveC(logo.c)
    const channel = CHANNEL_FRACTIONS[index % CHANNEL_FRACTIONS.length]
    const startU = logo.u + LOGO_SIZE / 2 + START_VIA_GAP
    // 45° knuckle: the slanted segment advances 1px along the flow axis per
    // 1px of cross-axis travel, so it spans |Δc| in u.
    const bendSpan = Math.abs(target - logoC)
    const bendStartU = Math.max(
      startU + ELBOW_RADIUS,
      Math.min(
        CLUSTER_DEPTH + zone * channel,
        zoneEnd - bendSpan - ELBOW_RADIUS
      )
    )
    const waypoints = [
      { u: startU, c: logoC },
      { u: bendStartU, c: logoC },
      { u: bendStartU + bendSpan, c: target },
      { u: zoneEnd, c: target },
    ].map(({ u, c }) => toPixel(u, c, orientation, size, mirror))
    return {
      d: buildRoundedPath(waypoints, ELBOW_RADIUS),
      startVia: waypoints[0],
      endVia: waypoints[waypoints.length - 1],
    }
  })
}

const rand = (min: number, max: number) => min + Math.random() * (max - min)
const randIndex = (count: number) => Math.floor(Math.random() * count)
const easeInOutSine = (progress: number) =>
  0.5 - 0.5 * Math.cos(Math.PI * progress)

type DotSlot = { traceIndex: number; start: number; duration: number }

export function CircuitBoardConnector({
  orientation,
  targets,
  logos = AI_CLIENT_LOGOS,
  mirror = false,
  flow,
  spawnSource,
  signalDurationMs,
  relativeCross = false,
  viaSize = VIA_SIZE,
  endInset = END_INSET,
}: CircuitBoardConnectorProps) {
  const flowsOut = (flow ?? (mirror ? 'out' : 'in')) === 'out'
  // Gradient ids must be unique per instance: url(#) resolves document-wide,
  // so with several connectors mounted the trails of all but the first would
  // paint with another fan's gradient coordinates and vanish.
  const trailGradientId = useId().replace(/:/g, '')
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<Size | null>(null)

  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      setSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const traces = useMemo(
    () =>
      size
        ? computeTraces(
            size,
            orientation,
            targets,
            logos,
            mirror,
            relativeCross,
            endInset
          )
        : [],
    [size, orientation, targets, logos, mirror, relativeCross, endInset]
  )

  const pathEls = useRef<(SVGPathElement | null)[]>([])
  const pathLengths = useRef<number[]>([])
  const dotEls = useRef<(SVGCircleElement | null)[]>([])
  const trailEls = useRef<(SVGPathElement | null)[]>([])
  const trailGradientEls = useRef<(SVGLinearGradientElement | null)[]>([])
  const slots = useRef<(DotSlot | null)[]>(Array(MAX_DOTS).fill(null))
  const nextSpawnAt = useRef<number | null>(null)
  const pendingSpawns = useRef(0)
  const prevOrientation = useRef(orientation)
  const reducedMotion = useReducedMotion()

  useLayoutEffect(() => {
    if (!spawnSource) return undefined
    return spawnSource.subscribe((count) => {
      pendingSpawns.current += count
    })
  }, [spawnSource])

  // Ref callbacks don't re-fire when `d` changes on resize, so lengths must
  // be re-measured whenever the traces change.
  useLayoutEffect(() => {
    pathLengths.current = traces.map(
      (_, index) => pathEls.current[index]?.getTotalLength() ?? 0
    )
    if (prevOrientation.current !== orientation) {
      prevOrientation.current = orientation
      slots.current = Array(MAX_DOTS).fill(null)
      for (const dot of dotEls.current) dot?.setAttribute('opacity', '0')
      for (const trail of trailEls.current) trail?.setAttribute('opacity', '0')
    }
    slots.current.forEach((slot, slotIndex) => {
      const d = slot ? traces[slot.traceIndex]?.d : undefined
      if (d) trailEls.current[slotIndex]?.setAttribute('d', d)
    })
  }, [traces, orientation])

  useAnimationFrame((time) => {
    if (reducedMotion || traces.length === 0) return

    // Lazy init keeps Math.random out of render (SSR hydration safety)
    if (nextSpawnAt.current === null) {
      nextSpawnAt.current = time + rand(...FIRST_SPAWN_MS)
    }
    if (!spawnSource && time >= nextSpawnAt.current) {
      nextSpawnAt.current = time + rand(...SPAWN_GAP_MS)
      const freeSlot = slots.current.indexOf(null)
      if (freeSlot !== -1) {
        let traceIndex = randIndex(traces.length)
        const isBusy = slots.current.some(
          (slot) => slot?.traceIndex === traceIndex
        )
        if (isBusy) traceIndex = randIndex(traces.length)
        slots.current[freeSlot] = {
          traceIndex,
          start: time,
          duration: rand(...RUN_DURATION_MS),
        }
        trailEls.current[freeSlot]?.setAttribute('d', traces[traceIndex].d)
      }
    }
    if (spawnSource && pendingSpawns.current > 0) {
      const count = pendingSpawns.current
      pendingSpawns.current = 0
      // Conducted bursts spread over distinct idle traces so two signals of
      // one emit never stack on the same path.
      const busy = new Set(
        slots.current.flatMap((slot) => (slot ? [slot.traceIndex] : []))
      )
      const idle = traces
        .map((_, traceIndex) => traceIndex)
        .filter((traceIndex) => !busy.has(traceIndex))
      for (let spawned = 0; spawned < count && idle.length > 0; spawned++) {
        const freeSlot = slots.current.indexOf(null)
        if (freeSlot === -1) break
        const [traceIndex] = idle.splice(randIndex(idle.length), 1)
        slots.current[freeSlot] = {
          traceIndex,
          start: time,
          duration: signalDurationMs ?? rand(...RUN_DURATION_MS),
        }
        trailEls.current[freeSlot]?.setAttribute('d', traces[traceIndex].d)
      }
    }

    slots.current.forEach((slot, slotIndex) => {
      const dot = dotEls.current[slotIndex]
      const trail = trailEls.current[slotIndex]
      const gradient = trailGradientEls.current[slotIndex]
      if (!slot || !dot || !trail || !gradient) return
      const progress = (time - slot.start) / slot.duration
      if (progress >= 1) {
        slots.current[slotIndex] = null
        dot.setAttribute('opacity', '0')
        trail.setAttribute('opacity', '0')
        return
      }
      const pathEl = pathEls.current[slot.traceIndex]
      const length = pathLengths.current[slot.traceIndex]
      if (!pathEl || !length) return
      // Outbound flow runs the head from the server edge (length) to the logo
      // end (0); the trail always lags behind the head, toward the source.
      const eased = easeInOutSine(progress)
      const head = (flowsOut ? 1 - eased : eased) * length
      const point = pathEl.getPointAtLength(head)
      dot.setAttribute('cx', String(point.x))
      dot.setAttribute('cy', String(point.y))
      const fade = Math.min(1, Math.min(progress, 1 - progress) / FADE_FRACTION)
      dot.setAttribute('opacity', String(fade * DOT_OPACITY))

      const available = flowsOut ? length - head : head
      const trailLength = Math.min(
        TRAIL_LENGTH,
        length * TRAIL_MAX_FRACTION,
        available
      )
      if (trailLength < 1) {
        trail.setAttribute('opacity', '0')
        return
      }
      const tailAt = flowsOut ? head + trailLength : head - trailLength
      const dashStart = Math.min(head, tailAt)
      const tail = pathEl.getPointAtLength(tailAt)
      trail.setAttribute(
        'stroke-dasharray',
        `${trailLength} ${length + trailLength}`
      )
      trail.setAttribute('stroke-dashoffset', String(-dashStart))
      trail.setAttribute('opacity', String(fade * TRAIL_OPACITY))
      // Gradient runs tail (transparent) → head (opaque) in user space; a
      // straight gradient tracks the along-path fade because traces never
      // double back on themselves.
      gradient.setAttribute('x1', String(tail.x))
      gradient.setAttribute('y1', String(tail.y))
      gradient.setAttribute('x2', String(point.x))
      gradient.setAttribute('y2', String(point.y))
    })
  })

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="position-relative width-full height-full"
    >
      {size && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="position-coverer"
        >
          <svg
            width={size.w}
            height={size.h}
            className="position-coverer block"
          >
            <defs>
              {Array.from({ length: MAX_DOTS }, (_, index) => (
                <linearGradient
                  key={index}
                  id={`connector-trail-${trailGradientId}-${index}`}
                  gradientUnits="userSpaceOnUse"
                  ref={(el) => {
                    trailGradientEls.current[index] = el
                  }}
                >
                  <stop
                    offset="0"
                    stopOpacity={0}
                    style={{ stopColor: DOT_COLOR }}
                  />
                  <stop
                    offset="1"
                    stopOpacity={1}
                    style={{ stopColor: DOT_COLOR }}
                  />
                </linearGradient>
              ))}
            </defs>
            {traces.map((trace, index) => (
              <path
                key={index}
                ref={(el) => {
                  pathEls.current[index] = el
                }}
                d={trace.d}
                stroke={TRACE_COLOR}
                strokeWidth={TRACE_WIDTH}
                fill="none"
              />
            ))}
            {Array.from({ length: MAX_DOTS }, (_, index) => (
              <path
                key={index}
                ref={(el) => {
                  trailEls.current[index] = el
                }}
                stroke={`url(#connector-trail-${trailGradientId}-${index})`}
                strokeWidth={TRAIL_WIDTH}
                strokeLinecap="round"
                fill="none"
                opacity={0}
              />
            ))}
            {Array.from({ length: MAX_DOTS }, (_, index) => (
              <circle
                key={index}
                ref={(el) => {
                  dotEls.current[index] = el
                }}
                r={DOT_RADIUS}
                fill={DOT_COLOR}
                opacity={0}
              />
            ))}
            {traces.flatMap((trace, index) =>
              [trace.startVia, trace.endVia].map((via, viaIndex) => (
                <rect
                  key={`${index}-${viaIndex}`}
                  x={via.x - viaSize / 2}
                  y={via.y - viaSize / 2}
                  width={viaSize}
                  height={viaSize}
                  rx={2.6}
                  fill="white"
                  stroke={TRACE_COLOR}
                  strokeWidth={2}
                />
              ))
            )}
          </svg>
          {logos.map(({ Icon, node, u, c }, index) => {
            const resolvedC = relativeCross
              ? c * crossExtent(size, orientation)
              : c
            const center = toPixel(u, resolvedC, orientation, size, mirror)
            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: center.x - LOGO_SIZE / 2,
                  top: center.y - LOGO_SIZE / 2,
                  width: LOGO_SIZE,
                  height: LOGO_SIZE,
                }}
              >
                {node ?? (Icon ? <Icon size={LOGO_SIZE} /> : null)}
              </div>
            )
          })}
        </motion.div>
      )}
    </div>
  )
}
